import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  InfoBox,
  InfoWrapper,
  RHFCheckbox,
  RHFNumberTextField,
  RHFSelect,
} from 'components';
import {
  EffettivoPresunto,
  FenomenoIspezioneSpecialeIdraulico,
  ProtezioneAlPiedePileSpalle,
} from 'constants/inspections';
import {
  EstensioneProtezioneAlPiedeOptions,
  EvidenzeProfonditaDelleFondazioniOptions,
  ProtezioneAlPiedePileSpalleOptions,
  StatoConservazioneProtezionePiedeOptions,
} from 'constants/selectOptions';
import { useInspectionPath } from 'hooks';
import { INFO } from 'pages/Inspections/config/info';
import { useInspectionSelector } from 'stores';

// Stessa spaziatura di CheckboxContainer in
// L1/GeneralData/Accordion4HydraulicRisk/Accordion4HydraulicRiskL1.styles.js
// (nessuno .styles.js dedicato qui, il file ha solo questa coppia di checkbox).
const CheckboxContainer = styled('div')({
  marginLeft: '10px',
  height: '100%',
  display: 'flex',
  marginTop: 28,
  gap: 10,
});

const Accordion4ErosionL3 = () => {
  const { watch, setValue, formState } = useFormContext();
  const { dirtyFields } = formState;
  // e-2026-06-t21 — stesso pattern di L0/L1, vedi Accordion1GeneralInfoL3.jsx.
  const { inspectionDisabled } = useInspectionSelector();
  const { isViewingInspection } = useInspectionPath();
  const readOnly = inspectionDisabled || isViewingInspection;

  // Se Erosione non è tra i fenomeni scelti in "Analisi L3 - Fenomeno
  // analizzato" (Accordion1GeneralInfoL3.jsx, 577), i campi di questo
  // accordion vanno disabilitati.
  const watchAnalisiFenomenoAnalizzato = watch('analisiFenomenoAnalizzato');
  const hasErosione = watchAnalisiFenomenoAnalizzato?.includes(
    FenomenoIspezioneSpecialeIdraulico.Erosione
  );

  const watchMassimaProfonditaScavoTotale = watch(
    'massimaProfonditaScavoTotale'
  );
  const watchProfonditaFondazioneRispettoAlveo = watch(
    'profonditaFondazioneRispettoAlveo'
  );

  // 592 — autocompilazione da 591/159 (piano §4.5), sola lettura.
  // Issue #1021, riapertura 2026-09-21, punto 13 — stesso fix di
  // Accordion3OvertoppingL3.jsx/francoIdraulicoFP2 (vedi commento lì per il
  // meccanismo completo): la guardia era sull'isDirty di TUTTO il form L3,
  // che un cambio altrove (es. 577 in Accordion1) rimetteva a true
  // riattivando questo effetto anche senza tocchi ai due campi sorgente
  // sotto — sostituita con dirtyFields dei soli campi sorgente di QUESTO
  // calcolo.
  useEffect(() => {
    if (
      !dirtyFields.massimaProfonditaScavoTotale &&
      !dirtyFields.profonditaFondazioneRispettoAlveo
    ) {
      return;
    }
    const totale = parseFloat(watchMassimaProfonditaScavoTotale);
    const df = parseFloat(watchProfonditaFondazioneRispettoAlveo);

    if (!isNaN(totale) && !isNaN(df) && df !== 0) {
      // Bug "banner modifiche non salvate riappare subito dopo il
      // salvataggio" (utente, 2026-09-02) — stesso motivo di
      // Accordion3OvertoppingL3.jsx/francoIdraulicoFP2: .toFixed(2) da solo
      // restituisce una STRINGA, ma dopo il salvataggio il valore torna dal
      // BE come NUMERO JSON — stesso valore, tipo diverso, react-hook-form
      // lo considera dirty. Number() mantiene il tipo coerente col baseline.
      setValue('rapportoDsTotDf', Number((totale / df).toFixed(2)));
    } else {
      setValue('rapportoDsTotDf', '');
    }
  }, [
    watchMassimaProfonditaScavoTotale,
    watchProfonditaFondazioneRispettoAlveo,
    dirtyFields.massimaProfonditaScavoTotale,
    dirtyFields.profonditaFondazioneRispettoAlveo,
  ]);

  // Stessa logica di Accordion4HydraulicRiskL1.jsx: 159b (flag) non è più
  // scelto direttamente da select, ma derivato dalla coppia di checkbox
  // mutuamente esclusive Effettiva/Presunta.
  const watchProfonditaFondazioneRispettoAlveoEffettiva = watch(
    'profonditaFondazioneRispettoAlveoEffettiva'
  );
  const watchProfonditaFondazioneRispettoAlveoPresunta = watch(
    'profonditaFondazioneRispettoAlveoPresunta'
  );

  // Issue #1021, riapertura 2026-09-21, punto 13 — stesso fix dell'effetto
  // rapportoDsTotDf sopra: guardia sostituita da isDirty (tutto il form) a
  // dirtyFields delle due sole checkbox sorgente, immune a un dirty
  // scatenato altrove (es. 577) e già naturalmente false dopo il reset()
  // post-salvataggio.
  useEffect(() => {
    if (
      !dirtyFields.profonditaFondazioneRispettoAlveoEffettiva &&
      !dirtyFields.profonditaFondazioneRispettoAlveoPresunta
    ) {
      return;
    }
    // Issue #1021 punto 13 (riapertura 2026-10-01) — `''`, non `null`:
    // mappingInL3.js/normalizeL3Field rappresenta sempre "vuoto" come
    // stringa vuota nel baseline di reset() (coerente con tutti gli altri
    // campi scalari del form L3). Scrivendo `null` qui, scoprire entrambe
    // le checkbox dopo averne toccata una (dirtyFields le marca comunque,
    // anche se il valore finale torna a scoperto) lasciava
    // profonditaFondazioneRispettoAlveoFlag a `null` mentre il baseline
    // resta `''` — stesso identico meccanismo (rappresentazione diversa
    // dello stesso "vuoto") degli altri fix di questo file, ma non
    // ancora coperto perché questo campo non ha mai un proprio
    // RHFxxx/defaultValue: la sua unica fonte di verità è normalizeL3Field.
    !watchProfonditaFondazioneRispettoAlveoEffettiva &&
      !watchProfonditaFondazioneRispettoAlveoPresunta &&
      setValue('profonditaFondazioneRispettoAlveoFlag', '');

    watchProfonditaFondazioneRispettoAlveoEffettiva &&
      setValue(
        'profonditaFondazioneRispettoAlveoFlag',
        EffettivoPresunto.Effettivo
      );
    watchProfonditaFondazioneRispettoAlveoPresunta &&
      setValue(
        'profonditaFondazioneRispettoAlveoFlag',
        EffettivoPresunto.Presunto
      );
  }, [
    watchProfonditaFondazioneRispettoAlveoEffettiva,
    watchProfonditaFondazioneRispettoAlveoPresunta,
    dirtyFields.profonditaFondazioneRispettoAlveoEffettiva,
    dirtyFields.profonditaFondazioneRispettoAlveoPresunta,
  ]);

  const watchProtezioneAlPiedePileSpalle = watch('protezioneAlPiedePileSpalle');
  const watchProtezioneAlPiedePileSpalleSi =
    watchProtezioneAlPiedePileSpalle === ProtezioneAlPiedePileSpalle.Si;

  // 612/172 — attivabili solo se 171 = Si (piano §4.5).
  // Issue #1021, riapertura 2026-09-21, punto 13 — stesso fix degli altri
  // due effetti sopra: guardia sostituita da isDirty (tutto il form) a
  // dirtyFields del solo campo sorgente 171, così un dirty scatenato altrove
  // (es. 577) non svuota questi due campi né sporca isDirty senza alcun
  // tocco a "Protezione al piede delle pile e delle spalle del ponte".
  useEffect(() => {
    if (!dirtyFields.protezioneAlPiedePileSpalle) return;
    if (!watchProtezioneAlPiedePileSpalleSi) {
      setValue('estensioneProtezioneAlPiede', '');
      setValue('statoConservazioneProtezionePiede', '');
    }
  }, [
    watchProtezioneAlPiedePileSpalle,
    dirtyFields.protezioneAlPiedePileSpalle,
  ]);

  return (
    <Grid container spacing={2} mt={2}>
      {!hasErosione && (
        <Grid item xs={12}>
          <InfoBox
            color="warning.light"
            text={
              <>
                {
                  'Per poter modificare i campi del fenomeno Erosione è necessario selezionare la voce '
                }
                <b>{'Erosione'}</b>
                {' nel campo '}
                <b>{'Fenomeno analizzato'}</b>
                {" all'interno della sezione "}
                <b>{'L3 - Idraulica > 1. Informazioni Generali'}</b>
              </>
            }
          />
        </Grid>
      )}
      <InfoWrapper
        size={3}
        infoDrawer={true}
        drawerTitle={INFO.evidenzeProfonditaDelleFondazioni.drawerTitle}
        drawerText={INFO.evidenzeProfonditaDelleFondazioni.drawerText}
      >
        <RHFSelect
          name="evidenzeProfonditaDelleFondazioni"
          label={'Evidenze sulla profondità delle fondazioni*'}
          defaultValue={''}
          selectOptions={EvidenzeProfonditaDelleFondazioniOptions}
          disabled={!hasErosione || readOnly}
        />
      </InfoWrapper>

      <InfoWrapper size={3} infoMessage={INFO.profonditaScavoMax.infoMessage}>
        <RHFNumberTextField
          name="massimaProfonditaScavoLocalizzata"
          label={
            <>
              {`Massima profondità di scavo d`}
              <sub style={{ fontSize: 14 }}>s</sub> localizzata [m]*
            </>
          }
          decimalScale={2}
          defaultValue=""
          // Issue #1021, punto 19 — "Numerico (f4.2)" (ID158,
          // dati-piattaforma-campi.md): 4 cifre significative totali, 2
          // decimali → 2 cifre intere, campo non firmato.
          withValueLimit={({ value }) => value < 100}
          disabled={!hasErosione || readOnly}
        />
      </InfoWrapper>

      {/* Issue #1021, riapertura 2026-09-21, punto 17 — non wrappato in
      InfoWrapper come i due campi sulla stessa riga, restava 5px più in
      alto (stesso pattern di Accordion1GeneralInfoL3.jsx/
      Accordion2HydraulicDataL3.jsx/Accordion3OvertoppingL3.jsx). Nessun
      testo info esiste per questo campo. */}
      <InfoWrapper size={3}>
        <RHFNumberTextField
          name="massimaProfonditaScavoContrazione"
          label={
            <>
              {`Massima profondità di scavo d`}
              <sub style={{ fontSize: 14 }}>s</sub> contrazione [m]
            </>
          }
          decimalScale={2}
          defaultValue=""
          // Issue #1021, punto 19 — "Numerico (f4.2)" (ID589).
          withValueLimit={({ value }) => value < 100}
          disabled={!hasErosione || readOnly}
        />
      </InfoWrapper>
      <InfoWrapper
        size={3}
        infoMessage={INFO.massimaProfonditaScavoAltriFenomeni.infoMessage}
      >
        <RHFNumberTextField
          name="massimaProfonditaScavoAltriFenomeni"
          label={
            <>
              {`Massima profondità di scavo d`}
              <sub style={{ fontSize: 14 }}>s</sub> altri fenomeni [m]
            </>
          }
          decimalScale={2}
          defaultValue=""
          // Issue #1021, punto 19 — "Numerico (f4.2)" (ID590).
          withValueLimit={({ value }) => value < 100}
          disabled={!hasErosione || readOnly}
        />
      </InfoWrapper>

      <InfoWrapper
        size={3}
        infoMessage={INFO.massimaProfonditaScavoTotale.infoMessage}
      >
        <RHFNumberTextField
          name="massimaProfonditaScavoTotale"
          label={'Massima profondità di scavo ds totale [m]*'}
          decimalScale={2}
          defaultValue=""
          // Issue #1021, punto 19 — "Numerico (f4.2)" (ID591).
          withValueLimit={({ value }) => value < 100}
          disabled={!hasErosione || readOnly}
        />
      </InfoWrapper>

      <InfoWrapper
        size={3}
        infoDrawer={true}
        drawerTitle={INFO.profonditaFondazioneRispettoAlveo.drawerTitle}
        drawerText={INFO.profonditaFondazioneRispettoAlveo.drawerText}
        legislationMessage={
          INFO.profonditaFondazioneRispettoAlveo.legislationMessage
        }
      >
        <RHFNumberTextField
          name="profonditaFondazioneRispettoAlveo"
          label={
            <>
              {`Profondità fondazione rispetto all'alveo d`}
              <sub style={{ fontSize: 14 }}>f</sub> [m]*
            </>
          }
          decimalScale={2}
          defaultValue=""
          // Issue #1021, punto 19 — "Numerico (f4.2)" (ID159).
          withValueLimit={({ value }) => value < 100}
          disabled={!hasErosione || readOnly}
        />
      </InfoWrapper>

      {/* Issue #1021, riapertura 2026-09-21, punto 17 — questa riga
      (checkbox Effettiva/Presunta + rapportoDsTotDf) non era wrappata in
      InfoWrapper come i due campi che la precedono sulla stessa riga
      (massimaProfonditaScavoTotale/profonditaFondazioneRispettoAlveo),
      restava 5px più in alto. Nessun testo info esiste per questi due
      campi. */}
      <InfoWrapper size={3}>
        <CheckboxContainer>
          <RHFCheckbox
            name="profonditaFondazioneRispettoAlveoEffettiva"
            label="Effettiva"
            disabled={
              !hasErosione ||
              readOnly ||
              watchProfonditaFondazioneRispettoAlveoPresunta
            }
            defaultValue={false}
          />
          <RHFCheckbox
            name="profonditaFondazioneRispettoAlveoPresunta"
            label="Presunta"
            disabled={
              !hasErosione ||
              readOnly ||
              watchProfonditaFondazioneRispettoAlveoEffettiva
            }
            defaultValue={false}
          />
        </CheckboxContainer>
      </InfoWrapper>
      <InfoWrapper size={3}>
        <RHFNumberTextField
          name="rapportoDsTotDf"
          label={
            <>
              {`Rapporto d`}
              <sub style={{ fontSize: 14 }}>s</sub>
              {`, tot, d`}
              <sub style={{ fontSize: 14 }}>f</sub>
            </>
          }
          decimalScale={2}
          defaultValue=""
          // Issue #1021, punto 19 — "Numerico (f5.2)" (ID592): 5 cifre
          // significative totali, 2 decimali → 3 cifre intere. Campo sempre
          // disabled (auto-calcolato), ma coerente con gli altri per limite.
          withValueLimit={({ value }) => value < 1000}
          disabled
        />
      </InfoWrapper>
      <InfoWrapper
        size={3}
        infoDrawer={true}
        drawerTitle={INFO.protezioneAlPiedePileSpalle.drawerTitle}
        drawerText={INFO.protezioneAlPiedePileSpalle.drawerText}
      >
        <RHFSelect
          name="protezioneAlPiedePileSpalle"
          label={'Protezione al piede delle pile e delle spalle del ponte*'}
          defaultValue={''}
          selectOptions={ProtezioneAlPiedePileSpalleOptions}
          disabled={!hasErosione || readOnly}
        />
      </InfoWrapper>

      {/* Issue #1021, riapertura 2026-09-21, punto 17 — non wrappato in
      InfoWrapper come protezioneAlPiedePileSpalle (prima) e
      statoConservazioneProtezionePiede (dopo) sulla stessa riga, restava
      5px più in alto. Nessun testo info esiste per questo campo. */}
      {watchProtezioneAlPiedePileSpalleSi && (
        <InfoWrapper
          size={3}
          infoDrawer={true}
          drawerTitle={INFO.estensioneProtezioneAlPiede.drawerTitle}
          drawerText={INFO.estensioneProtezioneAlPiede.drawerText}
        >
          <RHFSelect
            name="estensioneProtezioneAlPiede"
            label={'Estensione della protezione al piede*'}
            defaultValue={''}
            selectOptions={EstensioneProtezioneAlPiedeOptions}
            disabled={!hasErosione || readOnly}
          />
        </InfoWrapper>
      )}
      {watchProtezioneAlPiedePileSpalleSi && (
        <InfoWrapper
          size={3}
          infoDrawer={true}
          drawerTitle={INFO.statoConservazioneProtezionePiede.drawerTitle}
          drawerText={INFO.statoConservazioneProtezionePiede.drawerText}
        >
          <RHFSelect
            name="statoConservazioneProtezionePiede"
            label={'Stato di conservazione e adeguatezza opera*'}
            defaultValue={''}
            selectOptions={StatoConservazioneProtezionePiedeOptions}
            disabled={!hasErosione || readOnly}
          />
        </InfoWrapper>
      )}
    </Grid>
  );
};

export { Accordion4ErosionL3 };
